import { Component, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';

import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { MiTableComponent } from 'src/app/shared/mi-table/mi-table.component';
import { BulkRequestService } from 'src/app/services/bulk-request/bulk-request.service';
import { MsalService } from '@azure/msal-angular';
import { saveAs } from "file-saver";
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { SharedModule } from 'src/app/shared/shared.module';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { NgxSpinnerModule } from 'ngx-spinner';

interface ExcelRow {
  [key: string]: any;
}

@Component({
    selector: 'app-bulk-request',
    imports: [FormsModule, MiTableComponent, SharedModule, NgxSpinnerModule],
    templateUrl: './bulk-request.component.html',
    styleUrl: './bulk-request.component.scss'
})
export class BulkRequestComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MiTableComponent) miTable!: MiTableComponent;

  uploadForm: FormGroup;
  selectedFile: File | null = null;

  topData: ExcelRow[] = [];
  excelData: ExcelRow[] = [];

  searchText = '';
  fileName = '';
  progress = 0;
  processingMessage = '';
  isLoading = false;
  isFileConfirmed = false;

  selectedFiles: File[] = [];
  selectedFileNames: any[] = [];
  fileErrors: string = '';

  private readonly maxFileSize = 10 * 1024 * 1024;
  private readonly allowedFileTypes = [
    '.xlsx', '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'
  ];

  readonly firstTableValidationRules = {
    // '# of customers': [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)],
    // 'Adjustment Type': [Validators.required],
    // 'Amount': [Validators.required],
    // 'Any Special Instruction/Comments': [Validators.required,Validators.maxLength(500)],
    // 'Description':[Validators.required,Validators.maxLength(500)],

    '# of customers': [Validators.required, Validators.pattern(/^\d+$/)],
    'Adjustment Type': [Validators.required],
    'Amount': [Validators.required, Validators.pattern(/^-?\d+(\.\d{1,2})?$/)],
    'Any Special Instruction/Comments': [Validators.required,Validators.maxLength(100),  Validators.pattern(/^(?!\s*$).+/)],
    'Description':[Validators.required,Validators.maxLength(100),  Validators.pattern(/^(?!\s*$).+/)]
  };

  readonly tableValidationRules = {
  //   'Billing Site': [Validators.required, Validators.pattern(/^[0-9]+$/)],
  //   'MMS#': [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)],
  //   'Customer Name': [Validators.required],
  //   'Amount': [Validators.required],
  //   'Check Request': [Validators.required,this.checkRequestValidator()],
  //   'Adjustment Method': [Validators.required, this.creditOnlyValidator()],
  //   'AE/Sales Contact': [Validators.required,Validators.maxLength(50)],
  //   // 'Description':[Validators.required,Validators.maxLength(1000)],
  //  'Remit Address': [this.conditionalRemitAddressValidator(),Validators.maxLength(500)]

   'Billing Site': [Validators.required,Validators.pattern(/^\d+$/)],
   'MMS#': [Validators.required,Validators.pattern(/^[a-zA-Z0-9\W]+$/), Validators.maxLength(50)],
   // Pattern allows alphanumeric characters and special characters
   'Customer Name': [Validators.required,Validators.maxLength(50),Validators.pattern(/^(?!\s*$).+/)],
   'Amount': [Validators.required,Validators.pattern(/^-?\d+(\.\d{1,2})?$/)],
   // 'Amount': [Validators.required,Validators.pattern(/^-?\d+(\.\d+)?$/)],
 
   'Check Request': [Validators.required,this.checkRequestValidator(),Validators.pattern(/^(?!\s*$).+/)],
   'Adjustment Method': [Validators.required, this.creditOnlyValidator()],
   'AE/Sales Contact': [Validators.required,Validators.maxLength(50),Validators.pattern(/^[a-zA-Z ]*$/),Validators.pattern(/^(?!\s*$).+/)],
   //'Description':[Validators.required,Validators.maxLength(100)],
    'Remit Address': [this.conditionalRemitAddressValidator(),Validators.maxLength(100),Validators.pattern(/^(?!\s*$).+/)]
  };

  constructor(
    private fb: FormBuilder,
    private bulkRequestService: BulkRequestService,
    private msalService: MsalService,
    private toastr: ToastrService,
    private spinner: SpinnerService
  ) {
    this.uploadForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.resetUploadState();
  }

  async onFileChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !this.validateFile(file)) return;

    this.selectedFile = file;
    this.fileName = file.name;

    const isConfirmed = await this.showUploadConfirmation(file);
    if (isConfirmed) await this.handleFileProcessing(file);
    else this.clearFile();
  }

  private validateFile(file: File): boolean {
    const extension = this.getFileExtension(file.name);
    const isValidType = this.allowedFileTypes.some(type =>
      extension === type.replace('.', '') || file.type === type
    );

    if (!isValidType) {
      this.handleError('Invalid file type. Upload .xlsx or .xls.');
      return false;
    }

    if (file.size > this.maxFileSize) {
      this.handleError(`File size exceeds ${this.formatFileSize(this.maxFileSize)}`);
      return false;
    }

    return true;
  }

  private getFileExtension(name: string): string {
    return name?.split('.').pop()?.toLowerCase() || '';
  }

  private formatFileSize(bytes: number): string {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  }

  private async showUploadConfirmation(file: File): Promise<boolean> {
    const result = await Swal.fire({
      width: '450px',
      titleText: 'Upload Confirmation',
      html: `<div style="font-size: 16px;"><strong>Name:</strong> ${file.name}<br/><strong>Size:</strong> ${this.formatFileSize(file.size)}</div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Upload',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#fcb900'
    });

    return this.isFileConfirmed = result.isConfirmed;
  }

 private async handleFileProcessing(file: File): Promise<void> {
  if (!this.validateFile(file)) return;

  this.spinner.showSpinner(); 

  try {
    const arrayBuffer = await file.arrayBuffer();

    const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    const topHeaders = (jsonData[4] as any[])?.filter(Boolean) || [];
    const topValues = (jsonData[5] as any[]) || [];

    if (!topHeaders.length || !topValues.length) {
      await this.showValidationError('The top section of the Excel file is missing required data.');
      this.clearFile();
      return;
    }

    const hasExtraTopData = jsonData.slice(7, 3).some((row: any[]) => row?.some(cell => cell));
    if (hasExtraTopData) {
      await this.showValidationError('Top section should contain only one row. Remove any extra rows.');
      this.clearFile();
      return;
    }

    this.topData = [Object.fromEntries(topHeaders.map((h, i) => [h, topValues[i + 1] || '']))];

    const mainHeaders = (jsonData[8] as any[]).filter(Boolean);
    const allRows = (jsonData as any[][]).slice(9).filter(row => row?.some(cell => cell));

    if (allRows.length > 50) {
      await this.showValidationError('Excel file has more than 50 rows. Limit to 50.');
      this.clearFile();
      return;
    }

    this.excelData = allRows.map(row =>
      Object.fromEntries(mainHeaders.map((h, i) => [h, row[i + 1] || '']))
    );
    this.updateTopAmountFromTable();
    const isValid = await this.validationExcelFile();
    if (!isValid) this.clearFile();

  } catch (error) {
    this.handleError('Error processing file. Please check the format.');
  } finally {
    this.spinner.hideSpinner();
  }
}

  private async validationExcelFile(): Promise<boolean> {
    const top = this.topData[0];

    if (!top || Object.values(top).some(v => !v)) {
      await this.showValidationError('Fill in all required fields in top section.');
      return true;
    }

    if (!this.excelData.length || !this.excelData.some(row => Object.values(row).some(val => val))) {
      await this.showValidationError('Add at least one row of data in main table.');
      return false;
    }

    if (this.excelData.length > 50) {
      await this.showValidationError('Main table cannot exceed 50 rows.');
      return false;
    }

    const topValidation = this.validateColumns(Object.keys(top), Object.keys(this.firstTableValidationRules), 'Top Data');
    if (!topValidation.isValid) {
      await this.showValidationError(topValidation.message);
      return false;
    }

    const mainValidation = this.validateColumns(Object.keys(this.excelData[0]), Object.keys(this.tableValidationRules), 'Main Data');
    if (!mainValidation.isValid) {
      await this.showValidationError(mainValidation.message);
      return false;
    }

    const customerCount = parseInt(top['# of customers'], 10);
    if (isNaN(customerCount) || customerCount !== this.excelData.length) {
      await this.showValidationError(`Incorrect Customer Count: The value in '# of customers' is ${top['# of customers']}, but the uploaded file contains ${this.excelData.length} customer records.`);
      return false;
    }

    const allowedTypes = ['goodwill', 'claims', 'processing error', 'billing rate error', 'invoicing error', 'other'];
    const adjType = (top['Adjustment Type'] || '').toLowerCase().trim();
    if (!allowedTypes.includes(adjType)) {
      await this.showValidationError(`Invalid Adjustment Type: "${top['Adjustment Type']}".<br/><br/>Allowed values are:<br/>- Goodwill<br/>- Claims<br/>- Processing Error<br/>- Billing Rate Error<br/>- Invoicing Error<br/>- Other`);
      return false;
    }

    return true;
  }

  private validateColumns(actual: string[], required: string[], section: string): { isValid: boolean, message: string } {
    const missing = required.filter(col => !actual.includes(col));
    const extra = actual.filter(col => !required.includes(col));

    if (missing.length) return { isValid: false, message: `Missing columns in ${section}: ${missing.join(', ')}` };
    if (extra.length) return { isValid: false, message: `Extra columns in ${section}: ${extra.join(', ')}` };
    return { isValid: true, message: '' };
  }
    private validateMainTableValues(): boolean {
    for (let i = 0; i < this.excelData.length; i++) {
      const row = this.excelData[i];

      const checkRequest = String(row['Check Request'] || '').toLowerCase().trim();
      const remitAddress = String(row['Remit Address'] || '').trim();

      if (checkRequest === 'yes' && !remitAddress) {
        Swal.fire({
          icon: 'warning',
          title: 'Missing Remit Address',
          html: `<p>Row ${i + 1} has 'Check Request' marked as YES, but no 'Remit Address' is provided.</p>`,
          confirmButtonColor: '#f87171'
        });
        return false;
      }

      for (const key in this.tableValidationRules) {
        const rules = this.tableValidationRules[key] || this.tableValidationRules[key.toLowerCase()];
        if (!rules || rules.length === 0) continue;

        const control = new FormControl(row[key], rules);
        control.updateValueAndValidity();

        if (control.invalid) {
           // ✅ Add this block to handle 'notCredit' error
        if (control.errors?.['notCredit']) {
          this.handleError(`Row ${i + 1}: 'Adjustment Method' must be "Credit Only"`);
        }

        return false;
        };
      }
      //  for (const key in this.firstTableValidationRules) {
      //   const rules = this.firstTableValidationRules[key] || this.firstTableValidationRules[key.toLowerCase()];
      //   if (!rules || rules.length === 0) continue;

      //   const control = new FormControl(row[key], rules);
      //   control.updateValueAndValidity();

      //   if (control.invalid) {
      //      // ✅ Add this block to handle 'notCredit' error
     

      //   return false;
      //   };
      // }
    }
    return true;
  }


  // downloadTemplate(): void {
  //   const wb = XLSX.utils.book_new();
  //   const data = [
  //     ['# of customers', 'Adjustment Type', 'Description', 'Amount', 'Any Special Instruction/Comments'],
  //     ['', '', '', '', ''],
  //     ['', '', '', '', ''],
  //     ['Billing Site', 'MMS#', 'Customer Name', 'Amount', 'Check Request', 'Adjustment Method', 'AE/Sales Contact', 'Remit Address'],
  //     ['', '', '', '', '', '', '', '']
  //   ];

  //   const ws = XLSX.utils.aoa_to_sheet(data);
  //   ws['!cols'] = Array(8).fill({ wch: 20 });
  //   XLSX.utils.book_append_sheet(wb, ws, 'Template');
  //   XLSX.writeFile(wb, 'Bulk_Adjustment_Template.xlsx');

  //   Swal.fire({
  //     icon: 'success',
  //     title: 'Template Downloaded!',
  //     //html: `<p><strong>Instructions:</strong></p><ol><li>Do not change headers</li><li>Fill required fields</li><li>Max 50 rows</li></ol>`,
  //     confirmButtonColor: '#3b82f6'
  //   });
  // }

  submitRequest(): void {
    this.spinner.showSpinner();
    if (!this.topData?.length || !this.excelData?.length) {
      this.handleError('Please upload a valid Excel file first.');
      return;
    }
    const topRow = this.topData[0];
  for (const key in this.firstTableValidationRules) {
    const rules = this.firstTableValidationRules[key];
    const control = new FormControl(topRow[key], rules);
    control.updateValueAndValidity();
    if (control.invalid) {
      this.handleError(`Top Section Error: "${key}" is invalid or missing.`);
      return;
    }
  }
    if (!this.validateMainTableValues()) {
      this.handleError('Please fix validation errors in the table before submitting.');
      return;
    }

    
    const payload = {
      invoiceAdjustmentDetails: this.excelData.map(row => ({
        customerName: row['Customer Name'] || '',
        mms: row['MMS#'].toString() || '',
        billingSite: row['Billing Site'] || '',
        remitAddress: row['Remit Address'] || '',
        adjustmentMethod: row['Adjustment Method'] || '',
        ae: row['AE/Sales Contact'] || '',
        checkRequest: row['Check Request'] || '',
        totalCorrectCharges: row['Amount']
      })),
      descriptionComment: topRow['Description'] || '',
      customerCount: topRow['# of customers'] || 0,
      specialInstructions: topRow['Any Special Instruction/Comments'] || '',
      brAdjType: topRow['Adjustment Type'] || '',
      AdjNetAmount: topRow['Amount'],
      recordCreatedBy: this.msalService.instance.getActiveAccount().username.toString()
    };

    this.isLoading = false;

    this.bulkRequestService.saveBulkRequests(payload).subscribe({
      next: (response) => {
        this.spinner.hideSpinner();      

        if (this.selectedFiles.length > 0) {
          const formData = new FormData();
          this.spinner.showSpinner();
          this.selectedFiles.forEach((file: File) => formData.append('files', file));
          formData.append('adjReqId', response.adjReqId);

          this.bulkRequestService.uploadBillingAdjustmentRequestFiles(formData, response.adjReqId).subscribe({
          
            next: () => 
              {
                this.spinner.hideSpinner();                
                Swal.fire({
                  icon: 'success',
                  title: `Request submitted successfully (AdjReqId: ${response.adjReqId})`,
                  confirmButtonText: "OK",
                  //text: `Request submitted successfully (AdjReqId: ${response.adjReqId})`,
                  confirmButtonColor: '#4CAF50',
                  allowOutsideClick: false,
                })
                .then((res1) => {
                  this.resetUploadState();
                });
              },
            error: () => {
              this.spinner.hideSpinner();
              //this.handleError('Files failed to upload, but request was submitted.')
              Swal.fire({
                title: "Billing adjustment request created with Request Id-"+ response.adjReqId +" , Attachments could not be submitted. Please contact support to submit the attachments",
                icon: "error",
                confirmButtonText: "OK",
                allowOutsideClick: false,
                customClass: {
                 title: 'custom-title'
                          }
              })
              .then((res1) => {
                this.resetUploadState();
              });        
            }
          });
        }else{
          this.spinner.hideSpinner();
          Swal.fire({
            icon: 'success',
            title: `Request submitted successfully (AdjReqId: ${response.adjReqId})`,
            confirmButtonText: "OK",
            //text: `Request submitted successfully (AdjReqId: ${response.adjReqId})`,
            confirmButtonColor: '#4CAF50',
            allowOutsideClick: false,
          })
              .then((res1) => {
                this.resetUploadState();
                });          
        }
      
      },
      error: (response) => {
        this.spinner.hideSpinner();
        this.isLoading = false;
        //this.handleError('please give correct customer Data');
        //this.handleError('Upload Failed. Please check the data marked in red. Customer Name/Billing Site');
        Swal.fire({
          title:'Upload Failed. Please check the data marked in red. Customer Name/Billing Site',
          icon: "error",
          confirmButtonText: "OK",
          allowOutsideClick: false,
          customClass: {
           title: 'custom-title'
          }
        });
       
        if (response?.error?.result?.invoiceDetails?.length) {
          this.excelData = this.excelData.map((row, index) => {
            const backendRow = response.error.result.invoiceDetails[index];
            return {
              ...row,
              _backendValidation: {
                customerIsValid: backendRow.customerIsValid,
                billingSiteIsValid: backendRow.billingSiteIsValid
              }
            };
          });
        } else {
          this.spinner.hideSpinner();
          //this.handleError('Failed to submit request. Please try again.');
          Swal.fire({
            title: "Failed to submit request. Please try again.",
            icon: "error",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            customClass: {
             title: 'custom-title'
                      }
          });  
          
        }
      }
      // ,
      // complete: () =>
      //   {
      //     this.spinner.hideSpinner();
      //     this.isLoading = false
      //   } 
    });
  }
  onRowUpdate(updatedRow: any): void {
  // If this came from the main table (excelData), recalc top amount
  if (this.excelData.includes(updatedRow)) {
    this.updateTopAmountFromTable();
  }
}
  private updateTopAmountFromTable(): void {
  if (!this.excelData || this.excelData.length === 0) return;

  const sum = this.excelData.reduce((total, row) => {
    const val = parseFloat(row['Amount']);
    return total + (isNaN(val) ? 0 : val);
  }, 0);

  // Update the topData "Amount" field
  if (this.topData.length > 0) {
    this.topData[0]['Amount'] = sum.toFixed(2);
  }
}


  downloadTemplate(): void {
    this.spinner.showSpinner();
    this.bulkRequestService.DownloadTemplate().subscribe({
      next: data => {
        const filename = `Bulk_Adjustment_Template.xlsx`;
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        saveAs(blob, filename);
        this.spinner.hideSpinner();
        this.toastr.success('File Downloaded Successfully', 'Success');
      },
      error: () => {
        this.spinner.hideSpinner();
        this.toastr.error('Error downloading file', 'Error');
      }
    });
  }

  getFileDetails(event: Event): void {
    this.fileErrors = '';
    const input = event.target as HTMLInputElement;
    const files = input.files;
    const allowedTypes = ['.csv', '.pdf', '.txt', '.xlsx', '.eml', '.msg'];

    if (!files) return;

    for (let i = 0; i < files.length; i++) {
      if (this.selectedFiles.length >= 5) {
        this.fileErrors = 'You can only select 5 files';
        break;
      }

      const file = files[i];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const fileMb = file.size / (1024 ** 2);

      if (!allowedTypes.includes(`.${fileExtension}`)) {
        this.fileErrors = 'Invalid file type. Only .csv, .pdf, .txt, .xlsx, .eml, .msg are allowed.';
        break;
      }

      if (fileMb >= 500) {
        this.fileErrors = "Please select a file less than 500MB.";
        break;
      }

      this.selectedFiles.push(file);
      this.selectedFileNames.push(file.name);
    }
  }

  removeFile(index: number): void {
    this.fileErrors = '';
    this.selectedFiles.splice(index, 1);
    this.selectedFileNames.splice(index, 1);
  }

  uploadSelectedFiles(adjReqId: number): void {
    const formData = new FormData();
    this.selectedFiles.forEach(file => formData.append('files', file));
    formData.append('adjReqId', adjReqId.toString());

    this.bulkRequestService.uploadBillingAdjustmentRequestFiles(formData, adjReqId).subscribe({
      
      error: () => this.handleError('File upload failed.')
    });
  }

  clearFile(): void {
    if (this.fileInput?.nativeElement?.value) {
      this.fileInput.nativeElement.value = '';
    }
    this.resetUploadState();
  }

  cancelUpload(): void {
    this.clearFile();
  }

  reuploadFile(): void {
    this.clearFile();
  }

  reloadFile(): void {
    if (!this.selectedFile) return;   
    this.isLoading = false;
    
    this.progress = 0;
    this.processingMessage = 'Reloading file...';
    this.topData = [];
    this.excelData = [];
    this.handleFileProcessing(this.selectedFile);
  }

  private handleError(message: string): void {
    this.spinner.hideSpinner();
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: message,
      confirmButtonColor: '#3b82f6'
    });
  }

  private async showValidationError(message: string): Promise<void> {
    this.spinner.hideSpinner();
    await Swal.fire({
      icon: 'error',
      title: 'Template Mismatch',
      html: `<p>${message}</p><p>Please download the template and try again.</p>`,
      confirmButtonText: 'Download Template',
      confirmButtonColor: '#4CAF50',
      showCancelButton: true
    }).then(r => { if (r.isConfirmed) this.downloadTemplate(); });
  }

  private resetUploadState(): void {
    this.selectedFile = null;
    this.isFileConfirmed = false;
    this.fileName = '';
    this.isLoading = false;
    this.progress = 0;
    this.processingMessage = '';
    this.topData = [];
    this.excelData = [];
  }

  creditOnlyValidator() {
    return (control: FormControl) => {
      const value = control.value;
      return value && value.trim().toLowerCase() === 'credit only'
        ? null
        : { notCredit: true };
    };
  }
  conditionalRemitAddressValidator() {
  return (control: FormControl) => {
    const row = control?.parent?.value;

    if (!row) return null; // no parent yet (initial phase)

    const checkRequest = String(row['Check Request'] || '').toLowerCase().trim();
    const remitAddress = String(control.value || '').trim();

    if (checkRequest === 'yes' && !remitAddress) {
      return { remitRequired: true };
    }

    return null;
  };
  
  
}
getProgressClass(): string {
  const roundedProgress = Math.round(this.progress / 10) * 10; // Round to nearest 10
  return `progress-${roundedProgress}`;
}
checkRequestValidator() {
  return (control: FormControl) => {
    const value = String(control.value || '').trim().toLowerCase();

    // Allow only 'yes' or 'no'
    if (value && value !== 'yes' && value !== 'no') {
      return { invalidCheckRequest: true };
    }

    return null;
  };
}


}

